import {
    createContext,
    FormEvent,
    memo,
    MutableRefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { default as ReactGA4 } from 'react-ga4';
import { useBoolean, useDebounce } from 'react-use';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { ObjectUtils } from '@zmedia/bm-soccer-ui/utils';
import { useAsyncFn } from '@zmedia/bm-soccer-ui/hooks/useAsyncFn';
import { useOutsideClick, useFocus } from '@zmedia/bm-soccer-ui/hooks';
import { SearchServices } from '@zmedia/bm-soccer-ui/service/SearchService';
import { Element, IconButton } from '@zmedia/bm-soccer-ui/components';
import { ISuggest, TTabType } from './constant';
import SearchDropdown from './SearchDropdown';
import styles from './search.module.scss';

const DEFAULT_SUGGEST = {
    news: [],
    players: [],
    teams: [],
    tournaments: [],
};

export const SearchDropdownContext = createContext(0);

function Search({ showSearch }: { showSearch: boolean }) {
    const router = useRouter();
    const searchRef: MutableRefObject<HTMLElement | null> =
        useRef<HTMLElement>(null);
    const [state, contentFn] = useAsyncFn(SearchServices.search);
    const [keyword, setKeyword] = useState<string>('');
    const [show, setShow] = useBoolean(false);
    const [suggest, setSuggest] = useState<ISuggest>(DEFAULT_SUGGEST);
    const [activeTab, setActiveTab] = useState<TTabType>('news');
    const [isTracking, setTracking] = useState<boolean>(false);
    const currFocus = useFocus();

    const handleClickOutside = (isInside: boolean) => {
        if (!isInside) {
            setShow(false);
        }
    };

    const searchDebounce = () => {
        if (show) {
            contentFn(keyword);
        } else {
            resetSuggest();
        }
    };

    useOutsideClick(searchRef, handleClickOutside);
    useDebounce(searchDebounce, 200, [keyword]);

    useEffect(() => {
        if (state.value) {
            setSuggest(state.value.data);
        }
    }, [state.value]);

    const resetSuggest = () => {
        setTimeout(() => {
            setSuggest(DEFAULT_SUGGEST);
        }, 10);
    };

    const clickSubmit = (e: any) => {
        ReactGA4.event({
            category: 'mobile_search',
            action: 'clickButtonSearch',
        });
        handleSubmit(e);
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        return false;
    };

    const handleInput = (e: FormEvent<HTMLInputElement>) => {
        e.preventDefault();
        setKeyword(e.currentTarget.value);
    };

    const handleFocus = () => {
        setShow(true);
    };

    const handleBlur = (e: any) => {
        if (e.keyCode === 13) {
            e.target.blur();
            handleSubmit(e);
        }
    };

    useEffect(() => {
        if (!ObjectUtils.isEmpty(suggest) && !isTracking) {
            ReactGA4.event({
                category: 'mobile_search_soccer',
                action: 'typeSearch',
            });
            setTracking(true);
        }
    }, [suggest, isTracking]);

    useEffect(() => {
        if (!ObjectUtils.isEmpty(suggest)) {
            const tabList = Object.keys(suggest) as any;
            if (tabList.indexOf(activeTab) === -1) {
                setActiveTab(tabList[0]);
            }
        }
    }, [suggest]);

    useEffect(() => {
        if (show || showSearch) {
            contentFn(keyword);
        }
    }, [show, showSearch]);

    useEffect(() => {
        setTracking(false);
    }, [router.asPath]);

    return (
        <SearchDropdownContext.Provider value={currFocus as number}>
            <Element className={styles['search-soccer-box']}>
                <form onSubmit={handleSubmit}>
                    <Element
                        domRef={searchRef}
                        className={cn(styles['soccer-search-container'], {
                            [styles['is-show']]: show || showSearch,
                        })}
                    >
                        <IconButton
                            onClick={clickSubmit}
                            icon="search"
                            title="Tìm kiếm"
                        />
                        <input
                            type="text"
                            className={styles['text-search']}
                            placeholder="Tìm giải, đội, cầu thủ và tin tức"
                            onFocus={handleFocus}
                            value={keyword}
                            onChange={handleInput}
                            onKeyDown={handleBlur}
                        />
                        <SearchDropdown
                            keyword={keyword}
                            resetSuggest={resetSuggest}
                            suggest={suggest}
                            setActiveTab={setActiveTab}
                            activeTab={activeTab}
                        />
                    </Element>
                </form>
            </Element>
        </SearchDropdownContext.Provider>
    );
}

export default memo(Search);
